import { useState, useEffect } from 'react'
import { Wallet, RefreshCw, ExternalLink, AlertCircle, CheckCircle, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface WalletBalance {
  address: string
  name: string
  description: string
  balances: {
    token: string
    symbol: string
    balance: string
    formatted: string
    chain: string
    isLow?: boolean
    minRequired?: string
  }[]
}

interface ContractInfo {
  address: string
  name: string
  chain: string
  stats?: {
    label: string
    value: string
  }[]
}

export function WalletsPage() {
  const [wallets, setWallets] = useState<WalletBalance[]>([])
  const [contracts, setContracts] = useState<ContractInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBalances = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.getWalletBalances()
      setWallets(data.wallets || [])
      setContracts(data.contracts || [])
    } catch (err: any) {
      setError(err.message || 'Error desconocido')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchBalances()
  }, [])

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Dirección copiada')
  }

  const getExplorerUrl = (chain: string, type: 'address' | 'tx', value: string) => {
    if (chain === 'BSC') {
      return `https://bscscan.com/${type}/${value}`
    }
    return `https://wer2scan.com/uni2/${type}/${value}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Wallets y Contratos</h1>
          <p className="text-dark-400 text-sm mt-1">Vista general de todas las wallets y balances del sistema</p>
        </div>
        <button
          onClick={fetchBalances}
          disabled={isLoading}
          className="btn-secondary btn-sm flex items-center gap-2"
        >
          <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          Actualizar
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Wallets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isLoading ? (
          // Skeleton loaders
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-dark-700 rounded w-1/3 mb-4" />
              <div className="h-4 bg-dark-700 rounded w-2/3 mb-6" />
              <div className="space-y-3">
                <div className="h-10 bg-dark-700 rounded" />
                <div className="h-10 bg-dark-700 rounded" />
              </div>
            </div>
          ))
        ) : (
          wallets.map((wallet, idx) => (
            <div key={idx} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-uni-400" />
                    {wallet.name}
                  </h3>
                  <p className="text-xs text-dark-400 mt-1">{wallet.description}</p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-center gap-2 mb-4 p-2 bg-dark-800/50 rounded-lg">
                <code className="text-xs text-dark-300 flex-1 truncate">{wallet.address}</code>
                <button
                  onClick={() => copyAddress(wallet.address)}
                  className="p-1 hover:bg-dark-700 rounded"
                  title="Copiar"
                >
                  <Copy className="w-3.5 h-3.5 text-dark-400" />
                </button>
                <a
                  href={getExplorerUrl(wallet.balances[0]?.chain || 'UNI2', 'address', wallet.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-dark-700 rounded"
                  title="Ver en explorer"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-dark-400" />
                </a>
              </div>

              {/* Balances */}
              <div className="space-y-2">
                {wallet.balances.map((balance, bidx) => (
                  <div
                    key={bidx}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg",
                      balance.isLow ? "bg-red-500/10 border border-red-500/20" : "bg-dark-800/50"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-dark-700 text-dark-300">
                        {balance.chain}
                      </span>
                      <span className="text-sm text-dark-300">{balance.symbol}</span>
                    </div>
                    <div className="text-right">
                      <span className={cn(
                        "font-mono font-semibold",
                        balance.isLow ? "text-red-400" : "text-white"
                      )}>
                        {balance.formatted}
                      </span>
                      {balance.isLow && balance.minRequired && (
                        <p className="text-xs text-red-400">
                          Min: {balance.minRequired}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status indicator */}
              {wallet.balances.some(b => b.isLow) ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  Fondos insuficientes
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2 text-xs text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  Fondos OK
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Contracts Section */}
      {contracts.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-white mt-8">Smart Contracts</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {contracts.map((contract, idx) => (
              <div key={idx} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-white">{contract.name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-dark-700 text-dark-300 mt-1 inline-block">
                      {contract.chain}
                    </span>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-center gap-2 mb-4 p-2 bg-dark-800/50 rounded-lg">
                  <code className="text-xs text-dark-300 flex-1 truncate">{contract.address}</code>
                  <button
                    onClick={() => copyAddress(contract.address)}
                    className="p-1 hover:bg-dark-700 rounded"
                    title="Copiar"
                  >
                    <Copy className="w-3.5 h-3.5 text-dark-400" />
                  </button>
                  <a
                    href={getExplorerUrl(contract.chain, 'address', contract.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 hover:bg-dark-700 rounded"
                    title="Ver en explorer"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-dark-400" />
                  </a>
                </div>

                {/* Stats */}
                {contract.stats && contract.stats.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {contract.stats.map((stat, sidx) => (
                      <div key={sidx} className="bg-dark-800/50 rounded-lg p-3">
                        <p className="text-xs text-dark-400">{stat.label}</p>
                        <p className="font-mono font-semibold text-white">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
